import { BaseFeature } from '../../core/base-feature.js';

/**
 * KeywordDatabaseFeature - Keyword groups management (Frontend)
 * Handles: syncing keywords with desktop, responding to keyword operations
 */
export class KeywordDatabaseFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Response callbacks map
        this.responseCallbacks = new Map();

        // Change listeners
        this.changeCallbacks = new Set();
    }

    static getMetadata() {
        return {
            id: 'keyword-database',
            name: 'Keyword Database',
            version: 'v1.0',
            capabilities: [
                'get_all_groups',
                'upsert_group',
                'delete_group',
                'check_keywords',
                'scan_content'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[KeywordDatabase] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId } = message;
        this._validateInbound(action, payload); // contract check (dev/test only)

        // Check if this is a response to a pending request
        if (messageId && this.responseCallbacks.has(messageId)) {
            const callback = this.responseCallbacks.get(messageId);
            this.responseCallbacks.delete(messageId);
            callback(payload || message);
            return;
        }

        switch (action) {
            case 'get_all_groups_response':
            case 'get_enabled_groups_response':
            case 'upsert_group_response':
            case 'delete_group_response':
            case 'check_keywords_response':
            case 'scan_content_response':
                // Handle response if messageId wasn't matched
                if (payload.messageId && this.responseCallbacks.has(payload.messageId)) {
                    const callback = this.responseCallbacks.get(payload.messageId);
                    this.responseCallbacks.delete(payload.messageId);
                    callback(payload);
                }
                return;

            case 'keywords_changed':
                // Notification that keywords changed on desktop
                this.notifyChangeListeners(payload);
                return;

            default:
                console.warn(`[KeywordDatabase] Unknown action: ${action}`);
        }
    }

    /**
     * Register a callback for a specific message response
     */
    registerCallback(messageId, callback, timeoutMs = 10000) {
        this.responseCallbacks.set(messageId, callback);

        // Timeout cleanup
        setTimeout(() => {
            if (this.responseCallbacks.has(messageId)) {
                this.responseCallbacks.delete(messageId);
                callback({ success: false, error: 'Request timeout' });
            }
        }, timeoutMs);
    }

    /**
     * Subscribe to keyword changes
     */
    onChange(callback) {
        this.changeCallbacks.add(callback);
        return () => this.changeCallbacks.delete(callback);
    }

    notifyChangeListeners(data) {
        for (const callback of this.changeCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[KeywordDatabase] Error in change callback:', error);
            }
        }
    }

    // =====================================
    // API Methods
    // =====================================

    /**
     * Get all keyword groups from desktop
     */
    async getAllGroups() {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('get_all_groups', {});
            this.registerCallback(messageId, resolve);
        });
    }

    /**
     * Get only enabled keyword groups
     */
    async getEnabledGroups() {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('get_enabled_groups', {});
            this.registerCallback(messageId, resolve);
        });
    }

    /**
     * Add or update a keyword group
     */
    async upsertGroup(group) {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('upsert_group', group);
            this.registerCallback(messageId, resolve);
        });
    }

    /**
     * Delete a keyword group
     */
    async deleteGroup(groupId) {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('delete_group', { groupId });
            this.registerCallback(messageId, resolve);
        });
    }

    /**
     * Check text against keywords (quick check)
     */
    async checkKeywords(text) {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('check_keywords', { text });
            this.registerCallback(messageId, resolve);
        });
    }

    /**
     * Scan content for keywords (thorough check)
     */
    async scanContent(content, title = '', url = '') {
        return new Promise((resolve) => {
            const messageId = this.sendMessage('scan_content', { content, title, url });
            this.registerCallback(messageId, resolve);
        });
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[KeywordDatabase] Cleaning up...');
        this.responseCallbacks.clear();
        this.changeCallbacks.clear();
    }
}
